import type { SearchOptions } from "@workspace/types";
import { CACHE_TIERS } from "@workspace/types";
import { useDebounce } from "@workspace/ui/hooks/use-debounce";
import { useEffect, useRef } from "react";
import {
	getSearchOptions,
	SEARCH_CONFIG,
} from "@/lib/features/search/search-config";
import { trackSearch } from "@/lib/services/analytics";
import { trpc } from "@/lib/trpc/react";

// Hook for search functionality using tRPC
export function useSearch(
	query: string,
	options: SearchOptions = {},
): ReturnType<typeof trpc.search.global.useQuery> {
	// Use centralized search configuration with option to override
	const searchOptions = getSearchOptions(options);
	const { limit, enabled, debounceMs, minQueryLength } = searchOptions;

	const debouncedQuery = useDebounce(query, debounceMs);
	const trimmedQuery = debouncedQuery.trim();
	const lastTrackedQuery = useRef<string>("");

	// Track search when debounced query changes and meets minimum length
	useEffect(() => {
		if (
			enabled &&
			trimmedQuery.length >= minQueryLength &&
			trimmedQuery !== lastTrackedQuery.current
		) {
			trackSearch(trimmedQuery, "all");
			lastTrackedQuery.current = trimmedQuery;
		}
	}, [trimmedQuery, enabled, minQueryLength]);

	return trpc.search.global.useQuery(
		{
			q: trimmedQuery,
			limit: limit.toString(),
			artists: true,
			festivals: true,
			shows: true,
			users: true,
		},
		{
			enabled: enabled && trimmedQuery.length >= minQueryLength,
			...CACHE_TIERS.DYNAMIC, // Search results should use dynamic caching
			retry: (failureCount, error) => {
				// Don't retry on client errors (4xx)
				if (error?.message?.includes("4")) return false;
				return failureCount < 2;
			},
			// Keep previous data while fetching new results for better UX
			placeholderData: (previousData) => previousData,
		},
	);
}

// Hook for getting combined search results with computed totals
export function useSearchResults(query: string, options: SearchOptions = {}) {
	const searchQuery = useSearch(query, options);
	const minQueryLength =
		options.minQueryLength || SEARCH_CONFIG.MIN_QUERY_LENGTH;

	const typedData = searchQuery.data as
		| { users?: any[]; festivals?: any[]; artists?: any[]; shows?: any[] }
		| undefined;
	const userResults = typedData?.users || [];
	const festivalResults = typedData?.festivals || [];
	const artistResults = typedData?.artists || [];
	const showResults = typedData?.shows || [];
	const totalResults =
		userResults.length +
		festivalResults.length +
		artistResults.length +
		showResults.length;

	return {
		...searchQuery,
		userResults,
		festivalResults,
		artistResults,
		showResults,
		totalResults,
		hasResults: totalResults > 0,
		isEmpty:
			!searchQuery.isLoading &&
			totalResults === 0 &&
			query.trim().length >= minQueryLength,
	};
}

// Utility function to invalidate search cache using tRPC utils
export function useInvalidateSearch() {
	const utils = trpc.useUtils();

	return {
		invalidateAll: () => {
			utils.search.global.invalidate();
		},
		invalidateQuery: (query: string, options?: { limit?: number }) => {
			utils.search.global.invalidate({
				q: query.trim(),
				limit: (options?.limit || 10).toString(),
				artists: true,
				festivals: true,
				shows: true,
				users: true,
			});
		},
	};
}
